/**
 * Cushion — what a month of living costs, how long the safety net lasts, and where
 * spare money is sitting.
 *
 * Which categories are living costs is a judgement nothing can make for you — this
 * household's largest outgoing is a house build and its third largest is savings, and
 * both stop the week the income does, while a $46 rates bill does not. So it is a list
 * you tick, grouped as YNAB groups it, because you arrive with a list in your head and
 * hunt for each one.
 *
 * The safety-net figure never nags. It posts no shortfall against the plan, because at
 * any realistic saving rate full cover is years away, and a target that shows red every
 * month for years is one people stop seeing.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type CushionCategory,
  type CushionResponse,
  fetchCushion,
  money,
  queryKeys,
  setCategoryFlags,
  setSafetyNet,
} from '../api.ts'
import { Dropdown } from '../components/Dropdown.tsx'
import { fmt, fmtWhole } from '../lib/format.ts'

const MONTH_CHOICES = [3, 6, 12]

const REASONS: Record<string, string> = {
  past_target: 'past its target',
  beyond_the_bill: 'more than its bill',
  above_its_refill: 'above what it refills to',
  beyond_its_bills: 'more than a year of its bills',
  uncommitted: 'no target says stop',
}

export function Cushion() {
  const queryClient = useQueryClient()
  const cushion = useQuery({ queryKey: queryKeys.cushion, queryFn: fetchCushion })

  /*
   * Painted before the request goes out. A tick that waits on a round trip takes long
   * enough to read as a control that did not register — and the reflex is to click it
   * again, which undoes it.
   */
  const flags = useMutation({
    mutationFn: (input: { id: string; livingCost?: boolean; ringFenced?: boolean }) => {
      const { id, ...change } = input
      return setCategoryFlags(id, change)
    },
    onMutate: async ({ id, ...change }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.cushion })
      const previous = queryClient.getQueryData<CushionResponse>(queryKeys.cushion)
      if (previous) queryClient.setQueryData(queryKeys.cushion, patch(previous, id, change))
      return { previous }
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(queryKeys.cushion, context.previous)
    },
    onSuccess: (data) => queryClient.setQueryData(queryKeys.cushion, data),
  })
  const net = useMutation({
    mutationFn: setSafetyNet,
    onSuccess: (data) => queryClient.setQueryData(queryKeys.cushion, data),
  })

  const data = cushion.data
  const all = data?.groups.flatMap((g) => g.categories) ?? []
  const spare = all.filter((c) => c.spare.amount > 0 && !c.spare.withheld)
  const overfunded = spare.filter((c) => c.spare.ceiling !== null)
  const unclaimed = spare.filter((c) => c.spare.ceiling === null)
  const withheld = all.filter((c) => c.spare.withheld)

  return (
    <section className="flex flex-col gap-6 px-4 pt-[22px] pb-[60px] sm:px-7">
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 font-display text-[34px] font-normal leading-none">Cushion</h2>
        <p className="m-0 max-w-[640px] text-[14px] text-muted">
          If the income stopped, how long would the money last — and if you needed some tomorrow,
          where could it come from without breaking a bill?
        </p>
      </div>

      {cushion.isPending && <p className="text-[14px] text-muted">Adding it up…</p>}
      {cushion.isError && (
        <p className="rounded-card bg-blush-soft p-4 text-[14px] text-blush-deep">
          {cushion.error.message}
        </p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
              <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
                A month of living
              </span>
              <span className="font-display text-[30px] leading-none">
                {fmt(data.monthlyLiving)}
              </span>
              <p className="m-0 text-[12.5px] text-muted">
                The categories ticked below. Each counts the largest of its average over{' '}
                {data.window.months} {data.window.months === 1 ? 'month' : 'months'}, what is
                scheduled for it now, and what its YNAB target asks for in a month.
              </p>
            </div>

            <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
              <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
                The safety net
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-[200px] flex-1">
                  <Dropdown
                    label="Safety net category"
                    value={data.safetyNet?.categoryId ?? ''}
                    placeholder="Choose the category that holds it"
                    onChange={(categoryId) =>
                      net.mutate({ categoryId: categoryId || null, months: data.safetyNetMonths })
                    }
                    options={[
                      { value: '', label: 'None chosen' },
                      ...all
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((c) => ({ value: c.id, label: c.name })),
                    ]}
                  />
                </div>
                <div className="flex rounded-full bg-segment p-[3px]">
                  {MONTH_CHOICES.map((months) => (
                    <button
                      key={months}
                      type="button"
                      onClick={() =>
                        net.mutate({ categoryId: data.safetyNet?.categoryId ?? null, months })
                      }
                      className={`rounded-full px-3 py-1.5 text-[12px] ${
                        data.safetyNetMonths === months
                          ? 'bg-segment-active font-medium text-ink shadow-[var(--shadow-raise)]'
                          : 'text-muted hover:text-ink'
                      }`}
                    >
                      {months} months
                    </button>
                  ))}
                </div>
              </div>
              {data.safetyNet ? (
                <>
                  <p className="m-0 text-[15px]">
                    {data.monthlyLiving > 0 ? (
                      <>
                        {fmt(data.safetyNet.available)} would last about{' '}
                        <span className="font-medium">
                          {data.safetyNet.monthsCovered.toFixed(1)} months
                        </span>
                        .
                      </>
                    ) : (
                      'Tick the living costs below to see how long it would last.'
                    )}
                  </p>
                  <div className="h-2 overflow-hidden rounded-full bg-sand">
                    <div
                      className="h-full rounded-full bg-sky-deep"
                      style={{ width: `${data.safetyNet.percent}%` }}
                    />
                  </div>
                  <span className="text-[12px] text-muted">
                    {data.safetyNet.shortfall > 0
                      ? `${fmtWhole(data.safetyNet.shortfall)} more would cover ${data.safetyNet.months} months.`
                      : `Covers ${data.safetyNet.months} months.`}
                  </span>
                </>
              ) : (
                <p className="m-0 text-[12.5px] text-muted">
                  Choose the category that holds your emergency money.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="m-0 font-display text-[22px] font-normal">Money you could move</h3>
            <p className="m-0 max-w-[640px] text-[13px] text-muted">
              Measured against what each category's YNAB target says it is for, and never below a
              year of what is scheduled from it. Card payments are never offered — that money is
              owed.
            </p>
            <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
              <SpareList
                title="Holding more than it needs"
                caption="Past a target, a bill, or what it refills to."
                rows={overfunded}
                onFence={(id) => flags.mutate({ id, ringFenced: true })}
              />
              <SpareList
                title="Unclaimed"
                caption="A buffer with no end. Not wrong — just the first place to look."
                rows={unclaimed}
                onFence={(id) => flags.mutate({ id, ringFenced: true })}
              />
            </div>
            {withheld.length > 0 && (
              <p className="m-0 text-[12.5px] text-muted">
                And {fmt(withheld.reduce((sum, c) => sum + c.spare.amount, 0))} more in{' '}
                {withheld.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 ? ', ' : ''}
                    <button
                      type="button"
                      onClick={() => flags.mutate({ id: c.id, ringFenced: false })}
                      className="underline decoration-line-strong underline-offset-2 hover:text-ink"
                      title="Offer this again"
                    >
                      {c.name}
                    </button>
                  </span>
                ))}{' '}
                that you said is saved for something.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="m-0 font-display text-[22px] font-normal">What a month costs</h3>
            <p className="m-0 max-w-[640px] text-[13px] text-muted">
              Tick what you would still be paying if the income stopped.
            </p>
            <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
              {data.groups
                .filter((group) => !group.isCardPayments)
                .map((group) => (
                  <div
                    key={group.id}
                    className="flex flex-col rounded-card border border-line bg-card px-4 py-3"
                  >
                    <span className="pb-1 text-[11px] uppercase tracking-[0.1em] text-ink/55">
                      {group.name}
                    </span>
                    {group.categories.map((category) => (
                      <label
                        key={category.id}
                        className="flex cursor-pointer items-center gap-2.5 border-t border-line-faint py-1.5 text-[13px] first:border-t-0"
                      >
                        <input
                          type="checkbox"
                          checked={category.livingCost}
                          onChange={(event) =>
                            flags.mutate({ id: category.id, livingCost: event.target.checked })
                          }
                          className="accent-[var(--color-cta)]"
                        />
                        <span className="min-w-0 flex-1 truncate">{category.name}</span>
                        <span
                          className={category.monthlyCost > 0 ? 'text-ink-dim' : 'text-muted'}
                          title={`Average ${fmt(category.average)} · scheduled ${fmt(category.scheduledMonthly)} · target ${fmt(category.targetMonthly)} a month`}
                        >
                          {fmt(category.monthlyCost)}
                        </span>
                      </label>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </section>
  )
}

function SpareList({
  title,
  caption,
  rows,
  onFence,
}: {
  title: string
  caption: string
  rows: CushionCategory[]
  onFence: (id: string) => void
}) {
  const total = rows.reduce((sum, c) => sum + c.spare.amount, 0)
  return (
    <div className="flex flex-col gap-1 rounded-card border border-line bg-card px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13.5px] font-medium">{title}</span>
        <span className="text-[13.5px]">{fmt(total)}</span>
      </div>
      <span className="pb-1 text-[12px] text-muted">{caption}</span>
      {rows.length === 0 && <span className="text-[12.5px] text-muted">Nothing.</span>}
      {rows
        .slice()
        .sort((a, b) => b.spare.amount - a.spare.amount)
        .map((c) => (
          <div
            key={c.id}
            className="group flex items-center gap-2 border-t border-line-faint py-1.5 text-[13px]"
          >
            <span className="min-w-0 flex-1 truncate">
              {c.name}
              <span className="text-muted"> · {REASONS[c.spare.reason ?? ''] ?? ''}</span>
            </span>
            <button
              type="button"
              onClick={() => onFence(c.id)}
              className="rounded-full px-2 py-0.5 text-[11px] text-muted opacity-100 transition-opacity hover:bg-sand hover:text-ink lg:opacity-0 lg:group-hover:opacity-100"
              title="Saved for something — stop offering it"
            >
              Saved for something
            </button>
            <span className="text-sky-deep">{fmt(c.spare.amount)}</span>
          </div>
        ))}
    </div>
  )
}

function patch(
  data: CushionResponse,
  id: string,
  change: { livingCost?: boolean; ringFenced?: boolean },
): CushionResponse {
  const groups = data.groups.map((group) => ({
    ...group,
    categories: group.categories.map((c) =>
      c.id === id
        ? {
            ...c,
            ...(change.livingCost === undefined ? {} : { livingCost: change.livingCost }),
            ...(change.ringFenced === undefined
              ? {}
              : {
                  ringFenced: change.ringFenced,
                  spare: { ...c.spare, withheld: change.ringFenced && c.spare.amount > 0 },
                }),
          }
        : c,
    ),
  }))
  const monthlyLiving = groups
    .flatMap((g) => g.categories)
    .filter((c) => c.livingCost)
    .reduce((sum, c) => sum + c.monthlyCost, 0)
  return { ...data, groups, monthlyLiving: money(monthlyLiving) }
}
